import type { AdminUserRow, DashboardStats, FamilyMemberSummary, FamilySummary, LicenseStatus, UserSummary } from './types';

export function toNumber(value: unknown): number {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }
  return 0;
}

export function normalizeLicenseStatus(value: unknown): LicenseStatus | string {
  const raw = String(value ?? 'trial').trim().toLowerCase();
  if (raw === 'active') return 'unlocked';
  if (!raw) return 'trial';
  return raw;
}

export function statusLabel(value: unknown): string {
  const status = normalizeLicenseStatus(value);
  switch (status) {
    case 'trial': return 'Trial';
    case 'unlocked': return 'Aktiv';
    case 'expired': return 'Abgelaufen';
    case 'demo': return 'Demo';
    case 'disabled': return 'Gesperrt';
    case 'suspended': return 'Pausiert';
    case 'cancelled': return 'Gekündigt';
    case 'internal': return 'Intern';
    default: return String(status);
  }
}

export function statusTone(value: unknown): 'good' | 'warn' | 'bad' | 'info' | 'muted' {
  const status = normalizeLicenseStatus(value);
  if (status === 'unlocked' || status === 'active') return 'good';
  if (status === 'trial' || status === 'demo') return 'info';
  if (status === 'expired') return 'warn';
  if (status === 'disabled' || status === 'suspended' || status === 'cancelled') return 'bad';
  return 'muted';
}

function roleLabel(role: unknown): string {
  switch (String(role ?? 'viewer').trim().toLowerCase()) {
    case 'owner': return 'Inhaber';
    case 'parent': return 'Elternteil';
    case 'caregiver': return 'Babysitter / Betreuung';
    case 'viewer': return 'Nur ansehen';
    default: return String(role ?? 'viewer');
  }
}

function roleDescription(role: unknown): string {
  switch (String(role ?? 'viewer').trim().toLowerCase()) {
    case 'owner': return 'Vollzugriff inklusive Mitglieder und Rollen.';
    case 'parent': return 'Kann Kinder und Schlafdaten verwalten.';
    case 'caregiver': return 'Kann Schlafdaten erfassen, aber keine Kinder bearbeiten oder löschen.';
    case 'viewer': return 'Nur Lesezugriff.';
    default: return 'Legacy-Rolle. Bitte prüfen.';
  }
}

export function buildFamilies(rows: AdminUserRow[]): FamilySummary[] {
  const map = new Map<string, FamilySummary>();
  for (const row of rows) {
    if (!row.family_id) continue;
    const existing = map.get(row.family_id);
    const members = existing?.members ? [...existing.members] : [];
    if (row.family_member_id && !members.some((member) => member.id === row.family_member_id)) {
      const role = row.family_member_role ?? 'viewer';
      members.push({
        id: row.family_member_id,
        familyId: row.family_id,
        userId: row.user_id,
        displayName: row.display_name || row.email,
        email: row.email,
        role,
        roleLabel: roleLabel(role),
        roleDescription: roleDescription(role),
        customRoleLabel: row.family_member_custom_role_label ?? null,
        status: row.disabled_at ? 'user_disabled' : 'active',
        updatedAt: row.updated_at ?? null,
      });
    }

    const next: FamilySummary = {
      id: row.family_id,
      name: row.family_name || 'Unbenannte Familie',
      ownerUserId: row.owner_user_id ?? null,
      ownerEmail: row.owner_email ?? null,
      ownerDisplayName: row.owner_display_name ?? null,
      licenseStatus: normalizeLicenseStatus(row.license_status),
      trialStartedAt: row.trial_start ?? row.trial_started_at ?? null,
      trialEndsAt: row.trial_end ?? row.trial_ends_at ?? null,
      licenseExpiresAt: row.license_expires_at ?? null,
      licenseUpdatedAt: row.license_updated_at ?? null,
      licenseSource: row.license_source ?? null,
      memberCount: Math.max(existing?.memberCount ?? 0, toNumber(row.member_count)),
      childCount: Math.max(existing?.childCount ?? 0, toNumber(row.child_count)),
      members,
    };
    map.set(row.family_id, { ...existing, ...next });
  }
  return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'de'));
}

export function buildUsers(rows: AdminUserRow[]): UserSummary[] {
  const familyLookup = new Map(buildFamilies(rows).map((family) => [family.id, family]));
  const map = new Map<string, UserSummary>();
  for (const row of rows) {
    const id = row.user_id;
    if (!id) continue;
    const existing = map.get(id) ?? {
      id,
      email: row.email,
      displayName: row.display_name || row.email,
      createdAt: row.created_at ?? null,
      updatedAt: row.updated_at ?? null,
      disabledAt: row.disabled_at ?? null,
      families: [],
    };
    if (row.family_id) {
      const family = familyLookup.get(row.family_id);
      if (family && !existing.families.some((entry) => entry.id === family.id)) {
        existing.families.push(family);
      }
    }
    map.set(id, existing);
  }
  return [...map.values()].sort((a, b) => a.email.localeCompare(b.email, 'de'));
}

export function buildDashboard(rows: AdminUserRow[]): DashboardStats {
  const users = buildUsers(rows);
  const families = buildFamilies(rows);
  const childrenTotal = families.reduce((sum, family) => sum + family.childCount, 0);
  const membersTotal = families.reduce((sum, family) => sum + family.memberCount, 0);
  const count = (status: string) => families.filter((family) => normalizeLicenseStatus(family.licenseStatus) === status).length;
  const now = Date.now();
  const sevenDays = 1000 * 60 * 60 * 24 * 7;
  const endingSoon = families.filter((family) => {
    const target = family.trialEndsAt ?? family.licenseExpiresAt;
    if (!target) return false;
    const time = Date.parse(target);
    return Number.isFinite(time) && time >= now && time - now <= sevenDays;
  }).length;
  const noOwner = families.filter((family) => !family.ownerEmail).length;
  const openWarnings = [
    endingSoon > 0 ? `${endingSoon} Trial/Lizenz läuft innerhalb von 7 Tagen aus.` : null,
    noOwner > 0 ? `${noOwner} Familie(n) ohne ermittelbaren Owner.` : null,
    count('expired') > 0 ? `${count('expired')} abgelaufene Lizenz(en).` : null,
    count('disabled') > 0 ? `${count('disabled')} gesperrte Lizenz(en).` : null,
  ].filter(Boolean) as string[];

  return {
    usersTotal: users.length,
    familiesTotal: families.length,
    childrenTotal,
    membersTotal,
    licensesActive: count('unlocked'),
    licensesTrial: count('trial'),
    licensesExpired: count('expired'),
    licensesDisabled: count('disabled'),
    demoLicenses: count('demo'),
    openWarnings,
  };
}
